import { AppNotification } from '../models/notification.interface';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
] as const;

export type NotificationGroupLabel = 'Hoy' | 'Este mes' | string;

export interface NotificationGroup {
  label: NotificationGroupLabel;
  notifications: AppNotification[];
}

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isYesterday(date: Date, ref: Date): boolean {
  const yesterday = new Date(ref);
  yesterday.setDate(ref.getDate() - 1);
  return isSameDay(date, yesterday);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatHourMinute12(date: Date): string {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const meridiem = hours >= 12 ? 'p.m.' : 'a.m.';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${pad2(minutes)} ${meridiem}`;
}

/**
 * Devuelve el texto de fecha para mostrar en cada notificaci\u00f3n dentro del modal.
 * - Si la fecha es hoy: formato relativo tipo "Hace X min/h" (mismo comportamiento del pipe timeAgo).
 * - Si es ayer: "Ayer, h:mm a.m./p.m."
 * - Si es anterior: "DD/MM/YY, h:mm a.m./p.m."
 *
 * Nota: cuando devuelve null, el componente deber\u00e1 usar el pipe `timeAgo` sobre la fecha original.
 */
export function formatNotificationDate(
  createAt: string | null | undefined,
  now: Date = new Date()
): string | null {
  const date = toDate(createAt);
  if (!date) return null;

  if (isSameDay(date, now)) {
    return null;
  }

  if (isYesterday(date, now)) {
    return `Ayer, ${formatHourMinute12(date)}`;
  }

  const day = pad2(date.getDate());
  const month = pad2(date.getMonth() + 1);
  const year = pad2(date.getFullYear() % 100);
  return `${day}/${month}/${year}, ${formatHourMinute12(date)}`;
}

function getGroupLabel(date: Date, now: Date): string {
  if (isSameDay(date, now)) return 'Hoy';
  if (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth()
  ) {
    return 'Este mes';
  }
  return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

function getGroupSortKey(label: string, now: Date): number {
  if (label === 'Hoy') {
    return Number.MAX_SAFE_INTEGER;
  }
  if (label === 'Este mes') {
    return now.getFullYear() * 12 + now.getMonth();
  }
  const [monthName, yearStr] = label.split(' ');
  const monthIdx = MONTH_NAMES.findIndex((m) => m === monthName);
  const year = parseInt(yearStr, 10) || now.getFullYear();
  return year * 12 + (monthIdx >= 0 ? monthIdx : 0);
}

/**
 * Agrupa las notificaciones en "Hoy", "Este mes" y meses anteriores.
 * Orden descendente: Hoy \u2192 Este mes \u2192 meses m\u00e1s recientes \u2192 m\u00e1s antiguos.
 * Dentro de cada grupo las notificaciones se ordenan por fecha descendente.
 */
export function groupNotificationsByDate(
  notifications: AppNotification[] | null | undefined,
  now: Date = new Date()
): NotificationGroup[] {
  if (!notifications?.length) return [];

  const byLabel = new Map<string, AppNotification[]>();

  for (const notification of notifications) {
    const date = toDate(notification.createAt);
    const label = date ? getGroupLabel(date, now) : 'Hoy';
    const list = byLabel.get(label) ?? [];
    list.push(notification);
    byLabel.set(label, list);
  }

  const groups: NotificationGroup[] = Array.from(byLabel.entries()).map(
    ([label, list]) => ({
      label,
      notifications: list.slice().sort((a, b) => {
        const da = toDate(a.createAt)?.getTime() ?? 0;
        const db = toDate(b.createAt)?.getTime() ?? 0;
        return db - da;
      }),
    })
  );

  groups.sort(
    (a, b) => getGroupSortKey(b.label, now) - getGroupSortKey(a.label, now)
  );

  return groups;
}
